import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Recycle, Truck, Factory, FileCheck, Package, Building2, Flame, ClipboardCheck, Trash2, ArrowRight, ArrowUpRight, LucideIcon } from 'lucide-react';
import { OptimizedImage } from '../ui/OptimizedImage';
import { useJsonData } from '../../hooks/useJsonData';

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  Building2, Factory, Recycle, FileCheck, Flame, ClipboardCheck, Trash2, Package, Truck
};

interface ServiceItem {
  icon: string;
  title: string;
  description: string;
  image: string;
  slug: string;
}

interface ServicesData {
  services: ServiceItem[];
}

// Optimized animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

export function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const { data, loading } = useJsonData<ServicesData>('/data/services.json');

  const services = data?.services || [];

  return (
    <section ref={ref} id="services" className="py-20 lg:py-28 bg-white overflow-hidden relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-eco-green-50 border border-eco-green-200 text-eco-green-700 text-sm font-semibold rounded-full mb-6"
          >
            <Recycle className="w-4 h-4" />
            Our Services
          </motion.div>
          <h2 className="text-4xl sm:text-5xl font-bold text-eco-green-900 mb-4">
            End-to-End Waste Solutions
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            One partner. Complete compliance. Maximum recovery.
          </p>
        </motion.div>

        {/* Loading State - matches actual card dimensions to prevent CLS */}
        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="bg-gray-100 rounded-2xl animate-pulse overflow-hidden">
                <div className="h-44 bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Services Grid */}
        {!loading && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Recycle;
              return (
                <motion.div key={service.title} variants={itemVariants}>
                  <Link to={`/services/${service.slug}`} className="block h-full group">
                    <div className="h-full bg-white/90 backdrop-blur-sm border border-gray-100/80 rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-eco-green-100/50 hover:border-eco-green-200 hover:bg-white transition-all duration-300">
                      {/* Image */}
                      <div className="relative h-44 overflow-hidden">
                        <OptimizedImage
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-eco-green-900/80 via-eco-green-900/20 to-transparent" />
                        
                        {/* Icon Badge */}
                        <motion.div 
                          className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-xl shadow-lg"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                        >
                          <IconComponent className="w-6 h-6 text-eco-green-600" />
                        </motion.div>

                        {/* Arrow */}
                        <div className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ArrowUpRight className="w-5 h-5 text-white" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <h3 className="text-lg font-bold text-eco-green-900 mb-2 group-hover:text-eco-green-600 transition-colors flex items-center gap-2">
                          {service.title}
                          <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* View All Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center mt-12"
        >
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-eco-green-600 font-semibold hover:text-eco-green-700 transition-colors group"
          >
            View All Services
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
